/**
 * Global 401 error handler to prevent infinite retry loops
 */

// Global flag to track 401 handling state
let is401Handling = false;
let authErrorTimestamp = 0;

// Set of active request URLs to prevent duplicates
const activeRequests = new Set<string>();

export const auth401Handler = {
  // Check if we're currently handling a 401
  isHandling401: () => is401Handling,

  // Check if a request should be allowed
  shouldAllowRequest: (url: string): boolean => {
    // Don't allow any requests if handling 401
    if (is401Handling) {
      console.debug('[Auth401Handler] Blocking request due to 401 handling:', url);
      return false;
    }

    // Check if this exact request is already in flight
    if (activeRequests.has(url)) {
      console.debug('[Auth401Handler] Blocking duplicate request:', url);
      return false;
    }

    return true;
  },

  // Register a request as active
  registerRequest: (url: string) => {
    activeRequests.add(url);
  },

  // Unregister a request when complete
  unregisterRequest: (url: string) => {
    activeRequests.delete(url);
  },

  // Handle 401 error
  handle401: () => {
    // Prevent multiple 401 handlers within 5 seconds
    const now = Date.now();
    if (is401Handling || (authErrorTimestamp && now - authErrorTimestamp < 5000)) {
      console.debug('[Auth401Handler] Already handling 401, skipping');
      return;
    }

    console.warn('[Auth401Handler] Handling 401 error');
    is401Handling = true;
    authErrorTimestamp = now;

    // Clear all active requests
    activeRequests.clear();

    // Clear any pending retry timers
    if ((window as any).__permissionRetryTimer) {
      clearTimeout((window as any).__permissionRetryTimer);
      (window as any).__permissionRetryTimer = null;
    }
    if ((window as any).__userPermissionRetryTimer) {
      clearTimeout((window as any).__userPermissionRetryTimer);
      (window as any).__userPermissionRetryTimer = null;
    }

    // Dispatch event to notify components
    window.dispatchEvent(new CustomEvent('auth401', { detail: { timestamp: now } }));
  },

  // Reset 401 handling state (e.g., after successful login)
  reset: () => {
    console.debug('[Auth401Handler] Resetting 401 handler');
    is401Handling = false;
    authErrorTimestamp = 0;
    activeRequests.clear();
  },

  // Get debug info
  getDebugInfo: () => ({
    is401Handling,
    authErrorTimestamp,
    activeRequestCount: activeRequests.size,
    activeRequests: Array.from(activeRequests),
  }),
};

// Export for global access
if (typeof window !== 'undefined') {
  (window as any).__auth401Handler = auth401Handler;
}
